import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, UserCheck, ShoppingCart, IndianRupee, Percent,
  Download, RefreshCw, Search, ExternalLink, SlidersHorizontal,
} from 'lucide-react';
import { toastMessage } from '@/utils/toast-message';
import SettingsSlideModal from '@/components/Modal/SettingsSlideModal';
import { InfoTip } from '@/pages/reports/_Components/InfoTip';
import {
  getCustomerProfilesSummary,
  getCustomerProfiles,
  exportCustomerProfiles,
  getAgentProfileCoverage,
  exportAgentProfileCoverage,
  type CustomerProfileFilters,
  type CustomerProfileSummary,
  type CustomerProfileRow,
  type AgentCoverageRow,
} from '@/services/customerProfilesService';

const PAGE_SIZE = 50;

const BUCKET_OPTIONS = [
  { value: '', label: 'All buckets' },
  { value: 'regular', label: 'Regular' },
  { value: 'abandoned_cart', label: 'Abandoned Cart' },
  { value: 'reorder', label: 'Reorder' },
  { value: 'customer', label: 'Customer (ordered, not delivered)' },
  { value: 'rto', label: 'RTO' },
];

const GENDER_OPTIONS = [
  { value: '', label: 'All genders' },
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Other', label: 'Other' },
];

const inr = (n: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

const bucketBadge = (b: string | null) => {
  const map: Record<string, string> = {
    reorder: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
    rto: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    abandoned_cart: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  };
  const label = b || 'regular';
  const cls = map[b || ''] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${cls}`}>{label.replace('_', ' ')}</span>;
};

interface StatCardProps {
  icon: React.ReactNode;
  bgIcon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  info?: string;
  gradient: string;
}
const StatCard = ({ icon, bgIcon, label, value, sub, info, gradient }: StatCardProps) => (
  <div className={`relative overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 p-4 ${gradient}`}>
    <div className="absolute -bottom-3 -right-3 opacity-[0.12] dark:opacity-[0.08] pointer-events-none">
      {bgIcon}
    </div>
    <div className="relative flex items-center gap-4">
      <div className="p-3 rounded-lg bg-white/70 dark:bg-white/10 backdrop-blur-sm shrink-0">{icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1">
          {label}
          {info && <InfoTip text={info} />}
        </p>
        <p className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">{value}</p>
        {sub && <p className="text-xs text-gray-500 dark:text-gray-400">{sub}</p>}
      </div>
    </div>
  </div>
);

type Tab = 'agent' | 'profiles';

interface DraftFilters {
  search: string;
  date_from: string;
  date_to: string;
  lead_bucket: string;
  gender: string;
  age_min: string;
  age_max: string;
  weight_min: string;
  weight_max: string;
  agent: string;
  filled_only: boolean;
}

const emptyDraft: DraftFilters = {
  search: '', date_from: '', date_to: '', lead_bucket: '', gender: '',
  age_min: '', age_max: '', weight_min: '', weight_max: '', agent: '',
  filled_only: true,
};

export default function CustomerProfilesReport() {
  const [activeTab, setActiveTab] = useState<Tab>('agent');
  const [filters, setFilters] = useState<CustomerProfileFilters>({ filled_only: true });
  const [summary, setSummary] = useState<CustomerProfileSummary | null>(null);
  const [rows, setRows] = useState<CustomerProfileRow[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [agentRows, setAgentRows] = useState<AgentCoverageRow[]>([]);
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentExporting, setAgentExporting] = useState(false);
  const [agentDateFrom, setAgentDateFrom] = useState('');
  const [agentDateTo, setAgentDateTo] = useState('');

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draft, setDraft] = useState<DraftFilters>(emptyDraft);

  const fetchData = useCallback(async (f: CustomerProfileFilters, p: number) => {
    setLoading(true);
    try {
      const [sumRes, listRes] = await Promise.all([
        getCustomerProfilesSummary(f),
        getCustomerProfiles(f, p, PAGE_SIZE),
      ]);
      if (sumRes.success) setSummary(sumRes.data as CustomerProfileSummary);
      if (listRes.success) {
        setRows((listRes.data as CustomerProfileRow[]) || []);
        const anyRes = listRes as unknown as { total?: number; totalPages?: number };
        setTotal(anyRes.total || 0);
        setTotalPages(anyRes.totalPages || 0);
      }
    } catch (e) {
      toastMessage(e instanceof Error ? e.message : 'Failed to load customer profiles', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(filters, page);
  }, [filters, page, fetchData]);

  const fetchAgentCoverage = useCallback(async (dateFrom: string, dateTo: string) => {
    setAgentLoading(true);
    try {
      const res = await getAgentProfileCoverage(dateFrom || undefined, dateTo || undefined);
      if (res.success) setAgentRows((res.data as AgentCoverageRow[]) || []);
    } catch (e) {
      toastMessage(e instanceof Error ? e.message : 'Failed to load agent coverage', 'error');
    } finally {
      setAgentLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgentCoverage(agentDateFrom, agentDateTo);
  }, [agentDateFrom, agentDateTo, fetchAgentCoverage]);

  const agentNameOptions = Array.from(new Set(agentRows.map((a) => a.agent_name))).sort();

  const [agentTabFilter, setAgentTabFilter] = useState('');
  const filteredAgentRows = agentTabFilter
    ? agentRows.filter((a) => a.agent_name === agentTabFilter)
    : agentRows;

  const openFilters = () => {
    setDraft({
      search: filters.search || '',
      date_from: filters.date_from || '',
      date_to: filters.date_to || '',
      lead_bucket: filters.lead_bucket || '',
      gender: filters.gender || '',
      age_min: filters.age_min?.toString() ?? '',
      age_max: filters.age_max?.toString() ?? '',
      weight_min: filters.weight_min?.toString() ?? '',
      weight_max: filters.weight_max?.toString() ?? '',
      agent: filters.agent || '',
      filled_only: filters.filled_only ?? true,
    });
    setFiltersOpen(true);
  };

  const applyFilters = () => {
    const toNum = (s: string) => (s.trim() === '' ? undefined : Number(s));
    setPage(1);
    setFilters({
      search: draft.search.trim() || undefined,
      date_from: draft.date_from || undefined,
      date_to: draft.date_to || undefined,
      lead_bucket: draft.lead_bucket || undefined,
      gender: draft.gender || undefined,
      age_min: toNum(draft.age_min),
      age_max: toNum(draft.age_max),
      weight_min: toNum(draft.weight_min),
      weight_max: toNum(draft.weight_max),
      agent: draft.agent || undefined,
      filled_only: draft.filled_only,
    });
    setFiltersOpen(false);
  };

  const activeFilterCount = [
    filters.search, filters.date_from, filters.date_to, filters.lead_bucket, filters.gender,
    filters.age_min, filters.age_max, filters.weight_min, filters.weight_max, filters.agent,
  ].filter((v) => v !== undefined && v !== null && v !== '').length;

  const handleAgentExport = async () => {
    setAgentExporting(true);
    try {
      await exportAgentProfileCoverage(agentDateFrom || undefined, agentDateTo || undefined);
      toastMessage('Export downloaded', 'success');
    } catch (e) {
      toastMessage(e instanceof Error ? e.message : 'Export failed', 'error');
    } finally {
      setAgentExporting(false);
    }
  };

  const completeBarColor = (pct: number) =>
    pct >= 70 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500';

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportCustomerProfiles(filters);
      toastMessage('Export downloaded', 'success');
    } catch (e) {
      toastMessage(e instanceof Error ? e.message : 'Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Customer Profiles</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Health/demographic profiles filled by agents, and how many converted to orders.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700 mb-6">
        {([
          { key: 'agent', label: 'By Agent' },
          { key: 'profiles', label: 'Browse Profiles' },
        ] as { key: Tab; label: string }[]).map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === t.key
                ? 'border-brandColor text-brandColor'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'agent' && (
      <>
      {/* Profile completion by agent */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Profile Completion by Agent</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Leads owned per agent, and how many have a complete / incomplete / missing health profile.
              {(agentDateFrom || agentDateTo) && ' Date range scopes when the profile was filled in, not lead count.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select value={agentTabFilter} onChange={(e) => setAgentTabFilter(e.target.value)}
              className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 dark:text-white">
              <option value="">All agents</option>
              {agentNameOptions.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
            <input type="date" value={agentDateFrom} onChange={(e) => setAgentDateFrom(e.target.value)}
              title="Profile created on/after"
              className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 dark:text-white" />
            <span className="text-xs text-gray-400">to</span>
            <input type="date" value={agentDateTo} onChange={(e) => setAgentDateTo(e.target.value)}
              title="Profile created on/before"
              className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 dark:text-white" />
            <button
              onClick={handleAgentExport}
              disabled={agentExporting || agentRows.length === 0}
              className="flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" /> {agentExporting ? 'Exporting…' : 'Download CSV'}
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Agent</th>
                <th className="px-3 py-2 text-right">Leads Owned</th>
                <th className="px-3 py-2 text-right">Complete</th>
                <th className="px-3 py-2 text-right">Incomplete</th>
                <th className="px-3 py-2 text-right">Missing</th>
                <th className="px-3 py-2 text-left w-48">% Complete</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {agentLoading ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">Loading…</td></tr>
              ) : filteredAgentRows.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">No agents found.</td></tr>
              ) : filteredAgentRows.map((r) => (
                <tr key={r.agent_id} className="hover:bg-gray-50 dark:hover:bg-gray-900/40">
                  <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">{r.agent_name}</td>
                  <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-200">{r.total_leads.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right text-emerald-700 dark:text-emerald-400">{r.complete_count.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right text-amber-700 dark:text-amber-400">{r.incomplete_count.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right text-gray-500 dark:text-gray-400">{r.missing_count.toLocaleString()}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${completeBarColor(r.complete_pct)}`}
                          style={{ width: `${Math.min(100, r.complete_pct)}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-600 dark:text-gray-300 w-10 text-right">{r.complete_pct}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}

      {activeTab === 'profiles' && (
      <>
      {/* Browse actions */}
      <div className="flex items-center justify-end gap-2 mb-4">
        <button
          onClick={openFilters}
          className="relative flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <SlidersHorizontal className="h-4 w-4" /> Filters
          {activeFilterCount > 0 && (
            <span className="flex items-center justify-center h-4 min-w-[16px] px-1 text-[10px] font-semibold rounded-full bg-brandColor text-white">
              {activeFilterCount}
            </span>
          )}
        </button>
        <button
          onClick={() => fetchData(filters, page)}
          className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-brandColor text-white hover:opacity-90 disabled:opacity-50"
        >
          <Download className="h-4 w-4" /> {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard
          icon={<Users className="h-5 w-5 text-blue-600" />}
          bgIcon={<Users className="h-[72px] w-[72px] text-blue-600" />}
          gradient="bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/40 dark:to-gray-800"
          label="Total Profiles" value={summary ? String(summary.total_profiles) : '—'}
          sub={summary ? `${summary.filled_profiles} with data` : undefined}
          info="All customer health/demographic profiles matching the current filters, whether or not any field has been filled in yet."
        />
        <StatCard
          icon={<UserCheck className="h-5 w-5 text-indigo-600" />}
          bgIcon={<UserCheck className="h-[72px] w-[72px] text-indigo-600" />}
          gradient="bg-gradient-to-br from-indigo-50 to-white dark:from-indigo-950/40 dark:to-gray-800"
          label="Filled Profiles" value={summary ? String(summary.filled_profiles) : '—'}
          info="Profiles with at least one health/demographic field entered by an agent."
        />
        <StatCard
          icon={<ShoppingCart className="h-5 w-5 text-emerald-600" />}
          bgIcon={<ShoppingCart className="h-[72px] w-[72px] text-emerald-600" />}
          gradient="bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/40 dark:to-gray-800"
          label="Converted" value={summary ? String(summary.converted_profiles) : '—'}
          sub={summary ? `${summary.orders_after_profile} orders` : undefined}
          info="Filled profiles whose customer placed at least one order after the profile was created."
        />
        <StatCard
          icon={<Percent className="h-5 w-5 text-purple-600" />}
          bgIcon={<Percent className="h-[72px] w-[72px] text-purple-600" />}
          gradient="bg-gradient-to-br from-purple-50 to-white dark:from-purple-950/40 dark:to-gray-800"
          label="Conversion Rate" value={summary ? `${summary.conversion_pct}%` : '—'}
          sub="of filled profiles"
          info="Converted profiles divided by filled profiles, as a percentage."
        />
        <StatCard
          icon={<IndianRupee className="h-5 w-5 text-amber-600" />}
          bgIcon={<IndianRupee className="h-[72px] w-[72px] text-amber-600" />}
          gradient="bg-gradient-to-br from-amber-50 to-white dark:from-amber-950/40 dark:to-gray-800"
          label="Revenue" value={summary ? inr(summary.revenue_after_profile) : '—'}
          sub="orders after profile"
          info="Total value of orders placed after profiling, across filled profiles in the current filter."
        />
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-left">Phone</th>
                <th className="px-4 py-3 text-left">Gender / Age</th>
                <th className="px-4 py-3 text-left">Primary Goal</th>
                <th className="px-4 py-3 text-left">Owner</th>
                <th className="px-4 py-3 text-left">Bucket</th>
                <th className="px-4 py-3 text-left">Profiled</th>
                <th className="px-4 py-3 text-center">Lead</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">No profiles match these filters.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.profile_uuid} className="hover:bg-gray-50 dark:hover:bg-gray-900/40">
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{r.lead_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{r.phone}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                    {[r.gender, r.age ? `${r.age}y` : null].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300 max-w-[220px] truncate" title={r.primary_goal || ''}>
                    {r.primary_goal || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{r.owner_name || '—'}</td>
                  <td className="px-4 py-3">{bucketBadge(r.lead_bucket)}</td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{r.created_at ? r.created_at.slice(0, 10) : '—'}</td>
                  <td className="px-4 py-3 text-center">
                    {r.lead_uuid
                      ? <Link to={`/lead/${r.lead_uuid}`} target="_blank" className="inline-flex text-brandColor hover:opacity-80"><ExternalLink className="h-4 w-4" /></Link>
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400">
          <span>{total.toLocaleString()} profiles</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40">Prev</button>
            <span>Page {page} of {totalPages || 1}</span>
            <button disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>
      </>
      )}

      {/* Filters drawer */}
      <SettingsSlideModal
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="Filters"
        subtitle="Narrow the profiles by scope and demographics"
        icon={<SlidersHorizontal className="h-5 w-5" />}
        width="420px"
      >
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Search</label>
            <div className="flex">
              <input
                value={draft.search}
                onChange={(e) => setDraft((d) => ({ ...d, search: e.target.value }))}
                placeholder="Phone or customer name"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brandColor"
              />
              <Search className="h-4 w-4 -ml-8 mt-2.5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">From (profiled)</label>
              <input type="date" value={draft.date_from}
                onChange={(e) => setDraft((d) => ({ ...d, date_from: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">To (profiled)</label>
              <input type="date" value={draft.date_to}
                onChange={(e) => setDraft((d) => ({ ...d, date_to: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 dark:text-white" />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Bucket</label>
            <select value={draft.lead_bucket}
              onChange={(e) => setDraft((d) => ({ ...d, lead_bucket: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 dark:text-white">
              {BUCKET_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Gender</label>
            <select value={draft.gender}
              onChange={(e) => setDraft((d) => ({ ...d, gender: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 dark:text-white">
              {GENDER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Agent</label>
            <select value={draft.agent}
              onChange={(e) => setDraft((d) => ({ ...d, agent: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 dark:text-white">
              <option value="">All agents</option>
              {agentNameOptions.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Age min</label>
              <input type="number" value={draft.age_min} min={0}
                onChange={(e) => setDraft((d) => ({ ...d, age_min: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Age max</label>
              <input type="number" value={draft.age_max} min={0}
                onChange={(e) => setDraft((d) => ({ ...d, age_max: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 dark:text-white" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Weight min (kg)</label>
              <input type="number" value={draft.weight_min} min={0}
                onChange={(e) => setDraft((d) => ({ ...d, weight_min: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Weight max (kg)</label>
              <input type="number" value={draft.weight_max} min={0}
                onChange={(e) => setDraft((d) => ({ ...d, weight_max: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 dark:text-white" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <input type="checkbox" checked={draft.filled_only}
              onChange={(e) => setDraft((d) => ({ ...d, filled_only: e.target.checked }))} />
            Filled only
          </label>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-end gap-2 shrink-0">
          <button onClick={() => setFiltersOpen(false)}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
            Cancel
          </button>
          <button onClick={applyFilters}
            className="px-4 py-2 text-sm rounded-lg bg-brandColor text-white hover:opacity-90">
            Apply
          </button>
        </div>
      </SettingsSlideModal>
    </div>
  );
}
